import { type Organization, type Profile, type Project, type User } from "@overmap-ai/common"
import {
	APIError,
	BaseApiService,
	type GetS3UrlResponse,
	HttpMethod,
	RESET_STATE,
	selectCurrentUser,
	setCurrentUser,
	setCurrentUserProfile,
} from "@overmap-ai/core"
import { allSettled, flow } from "better-all"
import * as localforage from "localforage"

import { DEBUG } from "../../constants"
import { persistor, type RootState, setLoggedIn, setSessionExpired } from "../../store"
import { confirm } from "../../utils/alertDialog"
import { buildLoginUrl, buildLogoutUrl, clearLoginBounces, navigateAway } from "../authPages"
import type { AppSDK } from "../sdk"

export class MainService extends BaseApiService<RootState, AppSDK> {
	host = import.meta.env.REACT_APP_API_URL

	async fetchOrganizationData(organizationId: Organization["uuid"], signal?: AbortSignal) {
		const client = this.client

		const awaited = await allSettled(
			{
				async organizationAccesses() {
					return client.organizationAccesses.refreshStore(organizationId, this.$signal)
				},
				// Projects
				async projects() {
					return client.projects.refreshStore(organizationId, this.$signal)
				},
				async projectAccesses() {
					await this.$.projects
					return client.projectAccesses.refreshStore(organizationId, this.$signal)
				},

				// Asset Types
				async assetTypes() {
					return client.assetTypes.refreshStore(organizationId, this.$signal)
				},

				// Issue Types
				async issueTypes() {
					return client.issueTypes.refreshStore(organizationId, this.$signal)
				},

				// Forms
				async forms() {
					return client.forms.refreshStore(organizationId, this.$signal)
				},
				async formRevisions() {
					await this.$.forms
					return client.formRevisions.refreshStore(organizationId, this.$signal)
				},
				async formSubmissions() {
					await this.$.forms
					return client.formSubmissions.refreshStore(organizationId, this.$signal)
				},

				// Documents
				async documents() {
					return client.documents.refreshStore(organizationId, this.$signal)
				},

				// Teams
				async teams() {
					return client.teams.refreshStore(organizationId, this.$signal)
				},

				// Procedure Types
				async procedureTypes() {
					return client.procedureTypes.refreshStore(organizationId, this.$signal)
				},
				async procedureSteps() {
					await this.$.procedureTypes
					return client.procedureSteps.refreshStore(organizationId, this.$signal)
				},

				// Other
				async users() {
					return client.users.refreshStore(organizationId, this.$signal)
				},
			},
			{ debug: DEBUG, signal: signal },
		)

		console.debug("LOADED AWAITED ORGANIZATION DATA")

		return async () => {
			const {
				projects,
				assetTypes,
				issueTypes,
				forms,
				formRevisions,
				formSubmissions,
				documents,
				teams,
				procedureTypes,
				procedureSteps,
			} = awaited

			await flow(
				{
					// Projects
					async projectAttachments() {
						if (projects.status === "rejected") this.$end(undefined)
						return client.projectAttachments.refreshStore(organizationId, this.$signal)
					},

					// Asset Types
					async assetTypeAttachments() {
						if (assetTypes.status === "rejected") this.$end(undefined)
						return client.assetTypeAttachments.refreshStore(organizationId, this.$signal)
					},
					async assetTypeFields() {
						if (assetTypes.status === "rejected") this.$end(undefined)
						return client.assetTypeFields.refreshStore(organizationId, this.$signal)
					},
					async assetTypeFieldsAttachments() {
						await this.$.assetTypeFields
						return client.assetTypeFieldsAttachments.refreshStore(organizationId, this.$signal)
					},
					async assetTypeIdentifiers() {
						if (assetTypes.status === "rejected") this.$end(undefined)
						return client.assetTypeIdentifiers.refreshStore(organizationId, this.$signal)
					},
					async assetTypeStatuses() {
						if (assetTypes.status === "rejected") this.$end(undefined)
						return client.assetTypeStatuses.refreshStore(organizationId, this.$signal)
					},

					// Issue Types
					async issueTypeAttachments() {
						if (issueTypes.status === "rejected") this.$end(undefined)
						return client.issueTypeAttachments.refreshStore(organizationId, this.$signal)
					},
					async issueTypeFields() {
						if (issueTypes.status === "rejected") this.$end(undefined)
						return client.issueTypeFields.refreshStore(organizationId, this.$signal)
					},
					async issueTypeFieldsAttachments() {
						await this.$.issueTypeFields
						return client.issueTypeFieldsAttachments.refreshStore(organizationId, this.$signal)
					},
					async issueTypeIdentifiers() {
						if (issueTypes.status === "rejected") this.$end(undefined)
						return client.issueTypeIdentifiers.refreshStore(organizationId, this.$signal)
					},
					async issueTypeStatuses() {
						if (issueTypes.status === "rejected") this.$end(undefined)
						return client.issueTypeStatuses.refreshStore(organizationId, this.$signal)
					},
					async issueTypePriorities() {
						if (issueTypes.status === "rejected") this.$end(undefined)
						return client.issueTypePriorities.refreshStore(organizationId, this.$signal)
					},

					// Forms
					async formRevisionAttachments() {
						if (formRevisions.status === "rejected") this.$end(undefined)
						return client.formRevisionAttachments.refreshStore(organizationId, this.$signal)
					},
					async formIdentifiers() {
						if (forms.status === "rejected") this.$end(undefined)
						return client.formIdentifiers.refreshStore(organizationId, this.$signal)
					},
					async formSubscribers() {
						if (forms.status === "rejected") this.$end(undefined)
						return client.formSubscribers.refreshStore(organizationId, this.$signal)
					},
					async formSubmissionFieldValues() {
						if (formSubmissions.status === "rejected") this.$end(undefined)
						return client.formSubmissionFieldValues.refreshStore(organizationId, this.$signal)
					},
					async formSubmissionComments() {
						if (formSubmissions.status === "rejected") this.$end(undefined)
						return client.formSubmissionComments.refreshStore(organizationId, this.$signal)
					},
					async formSubmissionCommentAttachments() {
						await this.$.formSubmissionComments
						return client.formSubmissionCommentAttachments.refreshStore(organizationId, this.$signal)
					},
					async formSubmissionFieldValuesAttachments() {
						await this.$.formSubmissionFieldValues
						return client.formSubmissionFieldValuesAttachments.refreshStore(organizationId, this.$signal)
					},
					async formIdentifierValues() {
						if (formSubmissions.status === "rejected") this.$end(undefined)
						return client.formIdentifierValues.refreshStore(organizationId, this.$signal)
					},

					// Documents
					async documentAttachments() {
						if (documents.status === "rejected") this.$end(undefined)
						return client.documentAttachments.refreshStore(organizationId, this.$signal)
					},

					// Teams
					async teamMemberships() {
						if (teams.status === "rejected") this.$end(undefined)
						return client.teamMemberships.refreshStore(organizationId, this.$signal)
					},

					// Procedure Types
					async procedureTypeAttachments() {
						if (procedureTypes.status === "rejected") this.$end(undefined)
						return client.procedureTypeAttachments.refreshStore(organizationId, this.$signal)
					},
					async procedureTypeFields() {
						if (procedureTypes.status === "rejected") this.$end(undefined)
						return client.procedureTypeFields.refreshStore(organizationId, this.$signal)
					},
					async procedureTypeFieldsAttachments() {
						await this.$.procedureTypeFields
						return client.procedureTypeFieldsAttachments.refreshStore(organizationId, this.$signal)
					},
					async procedureStepFields() {
						if (procedureSteps.status === "rejected") this.$end(undefined)
						return client.procedureStepFields.refreshStore(organizationId, this.$signal)
					},
					async procedureStepFieldsAttachments() {
						await this.$.procedureStepFields
						return client.procedureStepFieldsAttachments.refreshStore(organizationId, this.$signal)
					},
					async procedureStepDefaultAssignees() {
						if (procedureSteps.status === "rejected") this.$end(undefined)
						return client.procedureStepDefaultAssignees.refreshStore(organizationId, this.$signal)
					},
					async procedureStepDefaultReviewers() {
						if (procedureSteps.status === "rejected") this.$end(undefined)
						return client.procedureStepDefaultReviewers.refreshStore(organizationId, this.$signal)
					},

					// Other
					async emailDomains() {
						return client.emailDomains.refreshStore(organizationId, this.$signal)
					},
				},
				{ debug: DEBUG, signal: signal },
			)
		}
	}

	async fetchProjectData(projectId: Project["uuid"], signal?: AbortSignal) {
		const client = this.client

		const awiated = await allSettled(
			{
				async assets() {
					return client.assets.refreshStore(projectId, this.$signal)
				},
				async issues() {
					return client.issues.refreshStore(projectId, this.$signal)
				},
				async procedures() {
					return client.procedures.refreshStore(projectId, this.$signal)
				},
				async procedureIniatives() {
					return client.procedureInitiatives.refreshStore(projectId, this.$signal)
				},
			},
			{ debug: DEBUG, signal: signal },
		)

		console.debug("LOADED AWAITED PROJECT DATA")

		return async () => {
			const { assets, issues, procedures } = awiated

			await flow(
				{
					// Assets
					async assetTypeFieldValues() {
						if (assets.status === "rejected") this.$end(undefined)
						return client.assetTypeFieldValues.refreshStore(projectId, this.$signal)
					},
					async assetTypeFieldValuesAttachments() {
						await this.$.assetTypeFieldValues
						return client.assetTypeFieldValuesAttachments.refreshStore(projectId, this.$signal)
					},
					async assetTypeIdentifierValues() {
						if (assets.status === "rejected") this.$end(undefined)
						return client.assetTypeIdentifierValues.refreshStore(projectId, this.$signal)
					},
					async assetAttachments() {
						if (assets.status === "rejected") this.$end(undefined)
						return client.assetAttachments.refreshStore(projectId, this.$signal)
					},
					async assetComments() {
						if (assets.status === "rejected") this.$end(undefined)
						return client.assetComments.refreshStore(projectId, this.$signal)
					},
					async assetCommentAttachments() {
						await this.$.assetComments
						return client.assetCommentAttachments.refreshStore(projectId, this.$signal)
					},

					// Issues
					async issueTypeFieldValues() {
						if (issues.status === "rejected") this.$end(undefined)
						return client.issueTypeFieldValues.refreshStore(projectId, this.$signal)
					},
					async issueTypeFieldValuesAttachments() {
						await this.$.issueTypeFieldValues
						return client.issueTypeFieldValuesAttachments.refreshStore(projectId, this.$signal)
					},
					async issueTypeIdentifierValues() {
						if (issues.status === "rejected") this.$end(undefined)
						return client.issueTypeIdentifierValues.refreshStore(projectId, this.$signal)
					},
					async issueAssociations() {
						if (issues.status === "rejected") this.$end(undefined)
						return client.issueAssociations.refreshStore(projectId, this.$signal)
					},
					async issueAttachments() {
						if (issues.status === "rejected") this.$end(undefined)
						return client.issueAttachments.refreshStore(projectId, this.$signal)
					},
					async issueComments() {
						if (issues.status === "rejected") this.$end(undefined)
						return client.issueComments.refreshStore(projectId, this.$signal)
					},
					async issueCommentAttachments() {
						await this.$.issueComments
						return client.issueCommentAttachments.refreshStore(projectId, this.$signal)
					},
					async issueUpdates() {
						if (issues.status === "rejected") this.$end(undefined)
						return client.issueUpdates.refreshStore(projectId, this.$signal)
					},

					// Projects
					async projectComments() {
						return client.projectComments.refreshStore(projectId, this.$signal)
					},
					async projectFiles() {
						return client.projectFiles.refreshStore(projectId, this.$signal)
					},

					// Geo Images
					async geoImages() {
						return client.geoImages.refreshStore(projectId, this.$signal)
					},

					// Agents
					async agents() {
						return client.agents.refreshStore(projectId, this.$signal)
					},

					// Procedures
					async procedureTypeFieldValues() {
						if (procedures.status === "rejected") this.$end(undefined)
						return client.procedureFieldValues.refreshStore(projectId, this.$signal)
					},
					async procedureTypeFieldValuesAttachments() {
						await this.$.procedureTypeFieldValues
						return client.procedureFieldValuesAttachments.refreshStore(projectId, this.$signal)
					},
					async procedureStepFieldValues() {
						if (procedures.status === "rejected") this.$end(undefined)
						return client.procedureStepFieldValues.refreshStore(projectId, this.$signal)
					},
					async procedureStepFieldValuesAttachments() {
						await this.$.procedureStepFieldValues
						return client.procedureStepFieldValuesAttachments.refreshStore(projectId, this.$signal)
					},
					async procedureStepFieldValuesReviews() {
						await this.$.procedureStepFieldValues
						return client.procedureStepFieldValuesReviews.refreshStore(projectId, this.$signal)
					},
					async procedureStepAssignees() {
						if (procedures.status === "rejected") this.$end(undefined)
						return client.procedureStepAssignees.refreshStore(projectId, this.$signal)
					},
					async procedureStepComments() {
						if (procedures.status === "rejected") this.$end(undefined)
						return client.procedureStepComments.refreshStore(projectId, this.$signal)
					},
					async procedureStepEvents() {
						if (procedures.status === "rejected") this.$end(undefined)
						return client.procedureStepEvents.refreshStore(projectId, this.$signal)
					},
					async procedureStepReviewers() {
						if (procedures.status === "rejected") this.$end(undefined)
						return client.procedureStepReviewers.refreshStore(projectId, this.$signal)
					},
				},
				{ debug: DEBUG, signal: signal },
			)
		}
	}

	/**
	 * Establishes whether the browser's session is still good, and who it belongs to.
	 *
	 * Session mode has no login form to fall back on, so this is the app's only chance to find out. It runs once, at
	 * boot, before anything renders -- see `App.tsx`, which will not show the router until it has finished.
	 *
	 * It records what it finds and does not navigate: that is left to the protected routes (`_loggedIn.tsx`), since
	 * public routes like an invite's `/verify-email` must render without a session.
	 */
	async bootstrapSession(): Promise<void> {
		// The whole store is persisted, this flag included, so a tab closed while the dialog was up would otherwise
		// rehydrate showing it -- and offline, where the check below cannot settle anything, it would stay up over a
		// "Sign in" button that cannot reach anything. It says something about this run, so this run starts it clean.
		this.dispatch(setSessionExpired(false))
		let currentUser: User | null = null

		try {
			currentUser = await this.client.users.get()
		} catch (error: unknown) {
			// Only an answer from the server settles anything. A request that never arrived -- offline, API down, DNS
			// -- says nothing about the session, and this app is expected to work offline: bouncing to a login page
			// that cannot load would strand a user who has a perfectly good session and unsynced work in the outbox.
			// So on anything but a 401 we keep the state we rehydrated and carry on.
			const status: number | undefined = error instanceof APIError ? error.status : undefined

			if (status !== 401) {
				console.warn("Could not verify the session; continuing with the persisted state.", error)
				return
			}

			console.debug("No session.")
			this.dispatch(setLoggedIn(false))
			// NOTE: The 401 handler sets the flag on every 401 response, including this one.
			//  So, once again (see the start of this method), we need to mark the session as non-expired.
			this.dispatch(setSessionExpired(false))
			return
		}

		this.dispatch(setCurrentUser(currentUser satisfies User))
		this.dispatch(setLoggedIn(true))
		// The session works, so whatever made an earlier boot bounce is over.
		clearLoginBounces()
	}

	/**
	 * Signs the user out, after asking about anything that would be lost.
	 *
	 * NOTE: The confirmation lives here rather than in the menu that used to own it because two other callers --
	 *  the service worker update in `AppUpdater` and the "Sign out" button on the error boundary -- reach this without
	 *  passing through that menu, and both discarded the outbox without a word.
	 */
	async logout(options: { confirmed?: boolean } = {}) {
		if (!options.confirmed && !(await this.confirmSignOut())) return

		// Clear local state first because anything left behind belongs to the user who is signing out.
		// The login page then tells the user that they signed out.
		// (If the session cannot be ended from here, the API's logout page ends it after the user clicks a button.)
		this.client.auth.clearAuth()
		let storageError: unknown = null
		try {
			this.dispatch(RESET_STATE())
			await persistor.flush()
			await persistor.purge()
			// For good measure
			await localforage.clear()
		} catch (error: unknown) {
			storageError = error
		}
		const sessionEnded: boolean = await this.client.auth.endSession()
		// The session ends even if the local data could not be removed. The error is still thrown instead of opening the
		// login page because the user must not think that the data is gone. (A later offline visit would show it.)
		if (storageError !== null) throw storageError
		navigateAway(sessionEnded ? buildLoginUrl(window.location.origin) : buildLogoutUrl())
	}

	/**
	 * Asks about unsynced work before it is thrown away. Signing out with an empty outbox costs nothing, so it asks
	 * nothing.
	 * @returns Whether to proceed with signing out.
	 */
	private async confirmSignOut(): Promise<boolean> {
		// NOTE: Only counts non-GET outbox items. A queued read is not work the user would miss.
		const unsyncedChanges = this.select((state) =>
			state.outbox.requests.filter((item) => item.request.method !== HttpMethod.GET),
		)

		if (unsyncedChanges.length === 0) return true

		return confirm({
			title: "Sign out",
			description:
				"You have unsynced changes in the outbox. Are you sure you want to sign out? All unsynced " +
				"changes will be lost forever.",
			size: "sm",
			action: "Discard and sign out",
		})
	}

	async replaceProfilePicture(file: File): Promise<Profile> {
		const { store } = this.client

		const currentUser = selectCurrentUser(store.getState())

		if (!currentUser) {
			throw new Error(`Expected currentUser to be defined, got ${currentUser}`)
		}

		const filePayload = await this.client.files.getFilePayload(file)
		const objectUrl = URL.createObjectURL(file)
		const currentProfile = currentUser.profile

		const updatedProfile: Profile = {
			object_url: objectUrl,
			file_sha1: filePayload.sha1,
			file_extension: filePayload.extension,
			file_name: file.name,
		}

		this.dispatch(setCurrentUserProfile(updatedProfile))

		try {
			const { instance, presigned_url } = await this.performRequest<{
				instance: Profile
				presigned_url: GetS3UrlResponse | null
			}>({
				method: HttpMethod.PATCH,
				url: "/authentication/users/profile-details/",
				payload: {
					file_sha1: filePayload.sha1,
					file_extension: filePayload.extension,
					file: filePayload,
				},
			})

			this.dispatch(setCurrentUserProfile(instance))

			if (presigned_url) {
				void this.client.files.processPresignedUrl(instance.file_sha1!, presigned_url)
			}

			return instance
		} catch (e: unknown) {
			this.dispatch(setCurrentUserProfile(currentProfile))
			throw e
		} finally {
			URL.revokeObjectURL(objectUrl)
		}
	}
}
